import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import AuthGuard from './AuthGuard';
import GuestGuard from './GuestGuard';
import RoleGuard from './RoleGuard';
import { Loader } from '../shared/atoms/loader';
import { MODULE, MODULE_PERMISSIONS, MODULE_MANAGE_PERMISSIONS } from '../shared/constants/permissions';

// Lazy-loaded pages
const LoginPage = lazy(() => import('../pages/login/LoginPage'));
const ResetPasswordPage = lazy(() => import('../pages/login/ResetPasswordPage'));
const UnauthorizedPage = lazy(() => import('../pages/unauthorized/UnauthorizedPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const ClientsPage = lazy(() => import('../pages/clients/ClientsPage'));
const ClientDetailPage = lazy(() => import('../pages/clients/pages/ClientDetailPage'));
const CreateClientPage = lazy(() => import('../pages/clients/pages/CreateClientPage'));
const IntegrationsPage = lazy(() => import('../pages/integrations/IntegrationsPage'));
const IntegrationDetailPage = lazy(() => import('../pages/integrations/pages/IntegrationDetailPage'));
const EventLogsPage = lazy(() => import('../pages/event-logs/EventLogsPage'));
const EventLogDetailPage = lazy(() => import('../pages/event-logs/pages/EventLogDetailPage'));
const SupportTicketsPage = lazy(() => import('../pages/support-tickets/SupportTicketsPage'));
const CreateTicketPage = lazy(() => import('../pages/support-tickets/pages/CreateTicketPage'));
const TicketDetailPage = lazy(() => import('../pages/support-tickets/pages/TicketDetailPage'));
const SubscriptionPlansPage = lazy(() => import('../pages/subscription-plans/SubscriptionPlansPage'));
const InvoiceDetailPage = lazy(() => import('../pages/subscription-plans/pages/InvoiceDetailPage'));
const SubscriptionDetailPage = lazy(() => import('../pages/subscription-plans/pages/SubscriptionDetailPage'));
const UsersPage = lazy(() => import('../pages/users/UsersPage'));
const CreateUserPage = lazy(() => import('../pages/users/pages/CreateUserPage'));
const UserDetailPage = lazy(() => import('../pages/users/pages/UserDetailPage'));
const ClientUserDetailPage = lazy(() => import('../pages/users/pages/ClientUserDetailPage'));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'));
const ProjectsPage = lazy(() => import('../pages/projects/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('../pages/projects/pages/ProjectDetailPage'));
const SourceConfigPage = lazy(() => import('../pages/source-config/SourceConfigPage'));
const RolesPage = lazy(() => import('../pages/roles/RolesPage'));
const SourceConfigDetailPage = lazy(() => import('../pages/source-config/pages/SourceConfigDetailPage'));

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<Loader fullPage size="lg" />}>{children}</Suspense>
);

export const router = createBrowserRouter([
  // Public — GuestGuard
  {
    element: <GuestGuard />,
    children: [
      {
        path: 'login',
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <SuspenseWrapper>
            <ResetPasswordPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Protected — AuthGuard
  {
    path: '/',
    element: <AuthGuard />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          // Dashboard — all authenticated users
          {
            path: 'dashboard',
            element: (
              <SuspenseWrapper>
                <DashboardPage />
              </SuspenseWrapper>
            ),
          },

          // Clients — requires CLIENTS_VIEW or CLIENTS_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.CLIENT}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.CLIENT]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'clients',
                element: (
                  <SuspenseWrapper>
                    <ClientsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                // Creating and editing are writes, so they take CLIENTS_MANAGE rather than the
                // view permission that opens the list — a URL is not a way past a hidden button.
                element: (
                  <RoleGuard
                    requiredPermissions={[MODULE_MANAGE_PERMISSIONS[MODULE.CLIENT]]}
                    fallback="/unauthorized"
                  />
                ),
                children: [
                  {
                    path: 'clients/create',
                    element: (
                      <SuspenseWrapper>
                        <CreateClientPage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    // Reuses the create form; a static segment outranks `clients/:id` in the matcher.
                    path: 'clients/:id/edit',
                    element: (
                      <SuspenseWrapper>
                        <CreateClientPage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: 'clients/:id',
                element: (
                  <SuspenseWrapper>
                    <ClientDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Users — requires USERS_VIEW or USERS_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.USER}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.USER]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'users',
                element: (
                  <SuspenseWrapper>
                    <UsersPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'users/create',
                element: (
                  <SuspenseWrapper>
                    <CreateUserPage />
                  </SuspenseWrapper>
                ),
              },
              {
                // Reuses the create form; a static segment outranks `users/:id` in the matcher.
                path: 'users/:id/edit',
                element: (
                  <SuspenseWrapper>
                    <CreateUserPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'users/:id',
                element: (
                  <SuspenseWrapper>
                    <UserDetailPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'client-users/:id',
                element: (
                  <SuspenseWrapper>
                    <ClientUserDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Integrations — requires INTEGRATIONS_VIEW or INTEGRATIONS_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.INTEGRATION}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.INTEGRATION]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'integrations',
                element: (
                  <SuspenseWrapper>
                    <IntegrationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'integrations/:id',
                element: (
                  <SuspenseWrapper>
                    <IntegrationDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Support Tickets — requires TICKETS_VIEW or TICKETS_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.SUPPORT}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.SUPPORT]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'support-tickets',
                element: (
                  <SuspenseWrapper>
                    <SupportTicketsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'support-tickets/create',
                element: (
                  <SuspenseWrapper>
                    <CreateTicketPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'support-tickets/:id',
                element: (
                  <SuspenseWrapper>
                    <TicketDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Subscription Plans — requires BILLING_VIEW or BILLING_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.SUBSCRIPTION}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.SUBSCRIPTION]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'subscription-plans',
                element: (
                  <SuspenseWrapper>
                    <SubscriptionPlansPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'invoices/:id',
                element: (
                  <SuspenseWrapper>
                    <InvoiceDetailPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'subscriptions/:id',
                element: (
                  <SuspenseWrapper>
                    <SubscriptionDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Event Logs — requires EVENT_LOGS_VIEW
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.EVENT_LOG}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.EVENT_LOG]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'event-logs',
                element: (
                  <SuspenseWrapper>
                    <EventLogsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'event-logs/:id',
                element: (
                  <SuspenseWrapper>
                    <EventLogDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Projects — requires PROJECTS_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.PROJECT}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.PROJECT]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'projects',
                element: (
                  <SuspenseWrapper>
                    <ProjectsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'projects/:id',
                element: (
                  <SuspenseWrapper>
                    <ProjectDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Role Management — requires ROLES_VIEW or ROLES_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.ROLE}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.ROLE]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'roles',
                element: (
                  <SuspenseWrapper>
                    <RolesPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Source Config — requires SYNC_MANAGE
          {
            element: (
              <RoleGuard
                requiredModule={MODULE.SYNC}
                requiredPermissions={MODULE_PERMISSIONS[MODULE.SYNC]}
                fallback="/unauthorized"
              />
            ),
            children: [
              {
                path: 'source-config',
                element: (
                  <SuspenseWrapper>
                    <SourceConfigPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'source-config/:id',
                element: (
                  <SuspenseWrapper>
                    <SourceConfigDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Profile — all authenticated users
          {
            path: 'profile',
            element: (
              <SuspenseWrapper>
                <ProfilePage />
              </SuspenseWrapper>
            ),
          },

          // Unauthorized
          {
            path: 'unauthorized',
            element: (
              <SuspenseWrapper>
                <UnauthorizedPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
    ],
  },

  // Catch-all
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
